import { useEffect, useRef, useState } from "react";
import { createRenderer, type SceneHandle } from "./renderer.js";
import { detectQuality, type Quality } from "./quality.js";
import { useSession } from "../state/session.js";
import { serverNow } from "../state/serverTime.js";
import { useT } from "../i18n/index.js";
import { create } from "zustand";
import { clearModelCache, pendingModelCount } from "../assets/loader.js";

/** The single renderer handle, published for feature scenes (base, later world map) to attach to. */
export const useSceneStore = create<{ handle: SceneHandle | null }>(() => ({ handle: null }));

const BERLIN = new Intl.DateTimeFormat("en-US", {
  timeZone: "Europe/Berlin",
  hour: "numeric",
  hourCycle: "h23",
});

/** 0 at noon, 1 at midnight, following the Berlin game day (R90 day/night). */
export function nightFactor(nowMs: number): number {
  const hour = Number(BERLIN.format(new Date(nowMs)));
  const d = Math.abs(hour - 12) / 12; // 0 at noon, 1 at midnight
  return Math.pow(Math.max(0, (d - 0.35) / 0.65), 1.4);
}

/** Full-viewport WebGL canvas under the HUD (R92). Pointer events pass through when a panel is open. */
export function SceneHost({ active }: { active: boolean }) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handleRef = useRef<SceneHandle | null>(null);
  const pref = useSession((s) => s.settings?.graphicsQuality ?? "auto");
  const [quality, setQuality] = useState<Quality>("medium");
  /** Bumped when the graphics context comes back: the canvas and the renderer are rebuilt from scratch. */
  const [generation, setGeneration] = useState(0);
  const [lost, setLost] = useState(false);
  const [fps, setFps] = useState(0);
  const [renderStats, setRenderStats] = useState({ drawCalls: 0, triangles: 0 });
  const params = typeof location !== "undefined" ? new URLSearchParams(location.search) : null;
  const debug = !!params?.has("debug");
  const nightOverride = params?.has("night") ? Number(params.get("night")) : null;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Probe a temporary canvas so WebGLRenderer can select the real context's attributes.
    const initial: Quality = pref === "auto" ? detectQuality() : pref;
    const handle = createRenderer(canvas, initial);
    handleRef.current = handle;
    useSceneStore.setState({ handle });
    setQuality(initial);
    const resize = () => handle.resize(canvas.clientWidth, canvas.clientHeight);
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    let lowFpsSeconds = 0;
    const off = handle.onFrame((dt) => {
      handle.setNight(
        nightOverride !== null && Number.isFinite(nightOverride) ? nightOverride : nightFactor(serverNow()),
      );
      // FPS governor: sustained < 24 fps on auto drops one quality tier (R92 budget).
      if (pref === "auto" && handle.fps > 0 && handle.fps < 24) {
        lowFpsSeconds += dt;
        if (lowFpsSeconds > 4 && handle.quality !== "low") {
          const next: Quality = handle.quality === "high" ? "medium" : "low";
          handle.setQuality(next);
          setQuality(next);
          lowFpsSeconds = 0;
        }
      } else lowFpsSeconds = 0;
    });
    const stats = setInterval(() => {
      setFps(handle.fps);
      setRenderStats({ drawCalls: handle.drawCalls, triangles: handle.triangles });
      window.__zend = {
        fps: handle.fps,
        quality: handle.quality,
        frames: handle.frames,
        night: nightOverride ?? nightFactor(serverNow()),
        drawCalls: handle.drawCalls,
        triangles: handle.triangles,
        pendingModels: pendingModelCount(),
      };
    }, 500);
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      clearInterval(stats);
      off();
      ro.disconnect();
      handle.dispose();
      handleRef.current = null;
      useSceneStore.setState({ handle: null });
    };
    // A phone browser drops the WebGL context under memory pressure (the picture goes white and never comes
    // back). Keeping the event's default tells the browser to restore it; everything on the dead context is
    // let go, including the model cache, and the scene is built again on a fresh canvas when it returns.
    const onLost = (event: Event) => {
      event.preventDefault();
      setLost(true);
      release();
      clearModelCache();
    };
    const onRestored = () => {
      setLost(false);
      setGeneration((g) => g + 1);
    };
    canvas.addEventListener("webglcontextlost", onLost);
    canvas.addEventListener("webglcontextrestored", onRestored);
    return () => {
      canvas.removeEventListener("webglcontextlost", onLost);
      canvas.removeEventListener("webglcontextrestored", onRestored);
      release();
    };
    // Quality changes are applied through the effect below; only a restored context rebuilds the renderer.
  }, [generation]);

  useEffect(() => {
    const h = handleRef.current;
    if (!h) return;
    const q: Quality = pref === "auto" ? detectQuality(canvasRef.current ?? undefined) : pref;
    if (q !== h.quality) {
      h.setQuality(q);
      setQuality(q);
    }
  }, [pref]);

  return (
    <>
      <canvas
        key={generation}
        ref={canvasRef}
        data-testid="scene-canvas"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
          touchAction: "none",
          pointerEvents: active ? "auto" : "none",
        }}
      />
      {lost ? (
        <div
          data-testid="scene-lost"
          role="status"
          style={{ position: "absolute", inset: 0, display: "grid", placeContent: "center", gap: 10, justifyItems: "center", textAlign: "center", padding: 24, background: "rgba(11,15,20,0.82)" }}
        >
          <strong>{t("scene.lost")}</strong>
          <span className="hint">{t("scene.lost.hint")}</span>
          <button type="button" className="btn" data-testid="scene-lost-retry" onClick={() => { setLost(false); setGeneration((g) => g + 1); }}>
            {t("scene.lost.retry")}
          </button>
        </div>
      ) : null}
      {debug ? (
        <div
          className="mono"
          style={{
            position: "absolute",
            left: 10,
            bottom: "calc(var(--safe-bottom) + 96px)",
            fontSize: 11,
            color: "var(--text-dim)",
            background: "rgba(0,0,0,0.5)",
            padding: "4px 8px",
            borderRadius: 6,
          }}
          data-testid="scene-debug"
        >
          {fps} fps · {t("scene.quality", { q: quality })}
          <br />
          {renderStats.drawCalls} draws · {Math.round(renderStats.triangles / 1000)}k triangles
        </div>
      ) : null}
    </>
  );
}
